// Preferences management

import { getAuthHeaders } from '../api/auth.js';
import { setUserCitationFormat, getUserCitationFormat } from './citations.js';
import { closePreferences } from './sidebar.js';

// Callback for loadResearchSession - will be set from pre/legacy.js
let loadResearchSessionCallback = null;

export function setPreferencesCallbacks(callbacks) {
    if (callbacks.loadResearchSession) {
        loadResearchSessionCallback = callbacks.loadResearchSession;
    }
}

export async function loadPreferences() {
    try {
        const response = await fetch('/api/auth/preferences', {
            headers: getAuthHeaders()
        });
        
        if (response.ok) {
            const data = await response.json();
            const citationFormat = data.citation_format || 'apa'; // Default to 'apa'
            const appearance = data.appearance || 'system'; // Default to 'system'
            
            // Update cached citation format
            setUserCitationFormat(citationFormat);
            
            // Set the active chip for citation format
            const citationChips = document.querySelectorAll('[data-format]');
            citationChips.forEach(chip => {
                chip.classList.remove('active');
                if (chip.dataset.format === citationFormat.toLowerCase()) {
                    chip.classList.add('active');
                }
            });
            
            // Fallback to APA if format not found
            if (!document.querySelector('[data-format].active')) {
                const apaChip = document.getElementById('citationFormatAPA');
                if (apaChip) apaChip.classList.add('active');
            }
            
            // Set the active chip for appearance
            const appearanceChips = document.querySelectorAll('[data-appearance]');
            appearanceChips.forEach(chip => {
                chip.classList.remove('active');
                if (chip.dataset.appearance === appearance.toLowerCase()) {
                    chip.classList.add('active');
                }
            });
            
            // Fallback to system if appearance not found
            if (!document.querySelector('[data-appearance].active')) {
                const systemChip = document.getElementById('appearanceSystem');
                if (systemChip) systemChip.classList.add('active');
            }
        } else {
            // If not authenticated or error, default to APA and System
            const apaChip = document.getElementById('citationFormatAPA');
            if (apaChip) apaChip.classList.add('active');
            const systemChip = document.getElementById('appearanceSystem');
            if (systemChip) systemChip.classList.add('active');
        }
    } catch (error) {
        console.error('[Preferences] Failed to load preferences:', error);
        // Default to APA and System on error
        const apaChip = document.getElementById('citationFormatAPA');
        if (apaChip) apaChip.classList.add('active');
        const systemChip = document.getElementById('appearanceSystem');
        if (systemChip) systemChip.classList.add('active');
    }
}

export async function applyPreferences(currentResearchId) {
    try {
        const selectedCitationChip = document.querySelector('[data-format].active');
        const selectedAppearanceChip = document.querySelector('[data-appearance].active');
        
        if (!selectedCitationChip) {
            alert('Please select a citation format');
            return;
        }
        
        if (!selectedAppearanceChip) {
            alert('Please select an appearance mode');
            return;
        }
        
        const citationFormat = selectedCitationChip.dataset.format;
        const appearance = selectedAppearanceChip.dataset.appearance;
        
        // Get the previous citation format to check if it changed
        const previousCitationFormat = await getUserCitationFormat();
        const citationFormatChanged = citationFormat !== previousCitationFormat;
        
        const headers = {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
        };
        
        const response = await fetch('/api/auth/preferences', {
            method: 'POST',
            headers,
            body: JSON.stringify({ 
                citation_format: citationFormat,
                appearance: appearance
            })
        });
        
        if (response.ok) {
            const result = await response.json();
            console.log('[Preferences] Preferences saved:', result);
            
            // Update cached citation format
            setUserCitationFormat(citationFormat);
            
            // Apply appearance theme (always apply, doesn't require reload)
            applyAppearanceTheme(appearance);
            
            // Only reload research session if citation format changed AND there's an active research session with search results
            if (citationFormatChanged && currentResearchId) {
                // Check if there are actually search results on the page before reloading
                const container = document.querySelector('.container');
                const hasSearchResults = container && container.querySelectorAll('.search-result-wrapper').length > 0;
                
                if (hasSearchResults) {
                    console.log('[Preferences] Citation format changed, reloading research session to update citations');
                    // Clear existing search results
                    const mainContent = document.getElementById('mainContent');
                    if (mainContent) {
                        // Remove all search result wrappers
                        const searchResultWrappers = mainContent.querySelectorAll('.search-result-wrapper');
                        searchResultWrappers.forEach(wrapper => wrapper.remove());
                    } else {
                        // Fallback to container if mainContent doesn't exist
                        if (container) {
                            const searchResultWrappers = container.querySelectorAll('.search-result-wrapper');
                            searchResultWrappers.forEach(wrapper => wrapper.remove());
                        }
                    }
                    
                    // Reload the research session via callback
                    if (loadResearchSessionCallback) {
                        await loadResearchSessionCallback(currentResearchId);
                    } else if (window.bms && window.bms.loadResearchSession) {
                        await window.bms.loadResearchSession(currentResearchId);
                    }
                } else {
                    console.log('[Preferences] Citation format changed but no search results on page, skipping reload');
                }
            } else if (citationFormatChanged) {
                console.log('[Preferences] Citation format changed but no active research session to reload');
            } else {
                console.log('[Preferences] Citation format unchanged, skipping research session reload');
            }
            
            // Close preferences sidebar
            closePreferences();
        } else {
            const error = await response.json();
            console.error('[Preferences] Failed to save preferences:', error);
            alert('Failed to save preferences: ' + (error.message || 'Unknown error'));
        }
    } catch (error) {
        console.error('[Preferences] Error saving preferences:', error);
        alert('Failed to save preferences: ' + error.message);
    }
}

// Apply appearance theme based on preference
export function applyAppearanceTheme(appearance) {
    const htmlElement = document.documentElement;
    const logo = document.getElementById('logo');
    
    // Store preference in localStorage for pre-paint application
    localStorage.setItem('bms_appearance', appearance);
    
    // Remove existing theme classes and data attributes
    htmlElement.classList.remove('force-dark', 'force-light');
    htmlElement.removeAttribute('data-theme');
    
    if (appearance === 'dark') {
        htmlElement.classList.add('force-dark');
        htmlElement.setAttribute('data-theme', 'dark');
        // Update logo to dark mode version
        if (logo) {
            logo.src = '/static/img/icon-darkmode.png';
        }
    } else if (appearance === 'light') {
        htmlElement.classList.add('force-light');
        htmlElement.setAttribute('data-theme', 'light');
        // Update logo to light mode version
        if (logo) {
            logo.src = '/static/img/icon.png';
        }
    } else {
        // System - remove forced classes and let CSS media query handle it
        htmlElement.setAttribute('data-theme', 'system');
        // Update logo based on system preference
        if (logo) {
            if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
                logo.src = '/static/img/icon-darkmode.png';
            } else {
                logo.src = '/static/img/icon.png';
            }
        }
    }
}

// Load and apply appearance theme on page load
export async function loadAndApplyAppearance() {
    try {
        const authHeaders = getAuthHeaders();
        // Only try to load if user is authenticated
        if (!authHeaders["X-Access-Token"]) {
            // Default to system if not authenticated
            applyAppearanceTheme('system');
            return;
        }
        
        const response = await fetch('/api/auth/preferences', {
            headers: authHeaders
        });
        
        if (response.ok) {
            const data = await response.json();
            const appearance = data.appearance || 'system';
            applyAppearanceTheme(appearance);
        } else {
            // Default to system if not authenticated or error
            applyAppearanceTheme('system');
        }
    } catch (error) {
        console.error('[Preferences] Failed to load appearance:', error);
        // Default to system on error
        applyAppearanceTheme('system');
    }
}
